import React from "react";
import { Star, Shield, Users, Heart, Award, Phone, Mail } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

const About = () => {
  const founders = [
    {
      name: "Military Leadership",
      background: "US Armed Forces Veterans",
      experience: "Decades of combined military service",
      values: "Honor, integrity, and service to community"
    }
  ];

  const timeline = [
    {
      year: "2020",
      event: "Company Founded",
      description: "Quality Transport Services established by military veterans"
    },
    {
      year: "2021", 
      event: "HIPAA Certification",
      description: "Achieved full HIPAA compliance and Business Associate certification"
    },
    {
      year: "2022",
      event: "Fleet Expansion",
      description: "Added wheelchair accessible vehicles to serve more clients"
    },
    {
      year: "2023",
      event: "Partnership Growth",
      description: "Established partnerships with major healthcare facilities"
    },
    {
      year: "2024",
      event: "Service Excellence",
      description: "Serving 500+ clients monthly with 99% satisfaction rate"
    }
  ];

  return (
    <div className="min-h-screen bg-white py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Star className="h-8 w-8 text-amber-500 fill-current" />
            <h1 className="text-4xl font-bold text-gray-900">About Quality Transport Services</h1>
            <Star className="h-8 w-8 text-amber-500 fill-current" />
          </div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Founded by military veterans and operated as a family business, we bring military precision, 
            family values, and unwavering commitment to medical transportation excellence in Michigan.
          </p>
        </div>

        {/* Founder Story */}
        <section className="mb-16">
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="text-2xl text-center flex items-center justify-center space-x-2">
                <Shield className="h-6 w-6 text-blue-600" />
                <span>Our Founder Story</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <div className="max-w-4xl mx-auto">
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Quality Transport Services was founded by United States military veterans who served our country 
                  with honor and distinction. After completing their military service, our founders recognized a 
                  critical need in their community: reliable, compassionate medical transportation for those who 
                  need it most.
                </p>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Drawing on military values of <strong>duty, honor, and service before self</strong>, they established 
                  Quality Transport Services in 2020 with a simple mission: treat every client like family and ensure 
                  no one misses their medical care due to transportation barriers.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  What started as a small, veteran-owned business has grown into a trusted partner for healthcare 
                  facilities throughout Calhoun County and beyond, while never losing sight of our core values: 
                  <em>integrity, reliability, and genuine care for every person we serve</em>.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Military Values */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Military Values in Action</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center border-green-200 bg-green-50">
              <CardContent className="p-8">
                <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Honor & Integrity</h3>
                <p className="text-gray-700">
                  We keep our promises, maintain the highest ethical standards, and treat every 
                  client with the respect and dignity they deserve.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-blue-200 bg-blue-50">
              <CardContent className="p-8">
                <Users className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Service Before Self</h3>
                <p className="text-gray-700">
                  Your needs come first. We go above and beyond to ensure safe, reliable 
                  transportation, even in challenging circumstances.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-purple-200 bg-purple-50">
              <CardContent className="p-8">
                <Award className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Excellence in All We Do</h3>
                <p className="text-gray-700">
                  Military precision meets family care. We maintain the highest standards 
                  in safety, compliance, and customer service.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Company Timeline */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Journey</h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 transform md:-translate-x-px top-0 bottom-0 w-0.5 bg-blue-600"></div>
            <div className="space-y-8">
              {timeline.map((item, index) => (
                <div key={index} className="relative flex items-center">
                  <div className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 w-8 h-8 bg-blue-600 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{index + 1}</span>
                  </div>
                  <div className={`ml-12 md:ml-0 ${index % 2 === 0 ? 'md:mr-1/2 md:pr-8' : 'md:ml-1/2 md:pl-8'}`}>
                    <Card className="hover:shadow-lg transition-shadow duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                            {item.year}
                          </span>
                          <h3 className="text-lg font-semibold text-gray-900">{item.event}</h3>
                        </div>
                        <p className="text-gray-700">{item.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Family Values */}
        <section className="mb-16">
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="text-2xl text-center flex items-center justify-center space-x-2">
                <Heart className="h-6 w-6 text-amber-600" />
                <span>Family-Operated with Heart</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    Being family-operated means more than just a business structure – it means we treat 
                    every client like a member of our extended family. Our drivers know many clients by name, 
                    remember their preferences, and genuinely care about their well-being.
                  </p>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    This personal touch, combined with military discipline and professionalism, creates 
                    a unique service experience that clients trust and healthcare providers recommend.
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center space-x-2">
                      <Heart className="h-4 w-4 text-red-500" />
                      <span>Personal relationships with clients and their families</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Heart className="h-4 w-4 text-red-500" />
                      <span>Flexible scheduling for family emergencies</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Heart className="h-4 w-4 text-red-500" />
                      <span>Going the extra mile when clients need us most</span>
                    </li>
                  </ul>
                </div>
                <div className="text-center">
                  <img
                    src="https://images.pexels.com/photos/24415252/pexels-photo-24415252.jpeg"
                    alt="Quality Transport Services vehicle"
                    className="rounded-lg shadow-xl w-full h-64 object-cover"
                  />
                  <p className="text-sm text-gray-600 mt-2 italic">
                    Our wheelchair accessible vehicles represent our commitment to serving everyone
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* By the Numbers */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Quality by the Numbers</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="text-center">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
                <div className="text-gray-600">Clients Served Monthly</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-green-600 mb-2">99%</div>
                <div className="text-gray-600">Client Satisfaction</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-purple-600 mb-2">5+</div>
                <div className="text-gray-600">Years of Service</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-amber-600 mb-2">100%</div>
                <div className="text-gray-600">HIPAA Compliant</div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Our Commitment */}
        <section className="mb-16">
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Our Commitment to You</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center max-w-4xl mx-auto">
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  <em>"We pledge to serve our community with the same dedication, honor, and commitment 
                  that we served our country. Every client matters, every trip is important, and every 
                  family deserves reliable transportation to healthcare."</em>
                </p>
                <p className="text-gray-600 font-semibold">
                  — The Founding Team, Quality Transport Services
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Contact CTA */}
        <div className="bg-blue-900 text-white rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">Experience the Quality Difference</h3>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Join hundreds of families who trust Quality Transport Services for their medical 
            transportation needs. Experience veteran-owned excellence and family-style care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600">
              <a href="/book-ride">Schedule Your Ride</a>
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-900">
              <Phone className="h-4 w-4 mr-2" />
              <a href="tel:(269) 248-3514">Call (269) 248-3514</a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;